import { Head, Heading, Markdown } from "zudoku/components";
import { CodeTabPanel, CodeTabs } from "zudoku/ui/CodeTabs.js";
import { SyntaxHighlight } from "zudoku/ui/SyntaxHighlight.js";
import type { ReactNode } from "react";
import {
  Example,
  Method,
  Param,
  ParamGroup,
  Params,
  Properties,
  Returns,
} from "../../JsReference";
import {
  exampleOf,
  notes,
  resolve,
  type Schemas,
  typeName,
  variantsOf,
} from "./schema";
import { authHeaders, curlSnippet, jsSnippet } from "./snippets";
import type { Operation, Parameter, Schema, TagDoc } from "./types";

const METHOD_COLOR: Record<string, string> = {
  GET: "text-emerald-600 dark:text-emerald-400",
  POST: "text-blue-600 dark:text-blue-400",
  PUT: "text-amber-600 dark:text-amber-400",
  PATCH: "text-amber-600 dark:text-amber-400",
  DELETE: "text-red-600 dark:text-red-400",
};

const MethodName = ({ method }: { method: string }) => (
  <span className={METHOD_COLOR[method] ?? "text-muted-foreground"}>{method}</span>
);

const Text = ({ content, notes: extra = [] }: { content?: string; notes?: string[] }) => {
  const body = [content, ...extra].filter(Boolean).join("\n\n");
  if (!body) return null;
  return /[`*_[<>#|\n]/.test(body) ? (
    <Markdown content={body} className="api-md" />
  ) : (
    <p>{body}</p>
  );
};

// Nesting stops here even when the schema goes on; deeper than this the
// reader has lost the thread, and a wide cyclic graph would grow the page
// without bound.
const MAX_DEPTH = 8;

const PARAM_GROUPS: { in: Parameter["in"]; title: string }[] = [
  { in: "path", title: "Path parameters" },
  { in: "query", title: "Query parameters" },
  { in: "header", title: "Header parameters" },
  { in: "cookie", title: "Cookie parameters" },
];

/*
  Fields of an object schema as <Param> rows. A field that is itself an object
  (or a list of objects, or a choice of objects) gets a nested box; a schema
  already open higher up in the tree is named but not opened again.
*/
const Fields = ({
  schema,
  schemas,
  depth,
  seen,
  request,
}: {
  schema: Schema;
  schemas: Schemas;
  depth: number;
  seen: ReadonlySet<string>;
  request: boolean;
}) => {
  const variants = variantsOf(schema);
  if (variants) {
    return (
      <>
        {variants.map((variant, i) => {
          const r = resolve(variant, schemas);
          return (
            <ParamGroup key={i} title={r.name ?? typeName(variant, schemas)} defaultOpen={i === 0}>
              <Fields schema={variant} schemas={schemas} depth={depth} seen={seen} request={request} />
            </ParamGroup>
          );
        })}
      </>
    );
  }

  const r = resolve(schema, schemas);
  const nextSeen = r.name ? new Set(seen).add(r.name) : seen;
  const required = new Set<string>(r.schema.required ?? []);
  const props = Object.entries(r.schema.properties ?? {}) as [string, Schema][];

  if (props.length === 0 && r.schema.additionalProperties && typeof r.schema.additionalProperties === "object") {
    props.push(["[key]", r.schema.additionalProperties]);
  }

  return (
    <>
      {props.map(([name, prop]) => (
        <Field
          key={name}
          name={name}
          schema={prop}
          schemas={schemas}
          required={required.has(name)}
          depth={depth}
          seen={nextSeen}
          request={request}
        />
      ))}
    </>
  );
};

/*
  The schema to open under a field, if any: the field's own object, the item
  of a list, or a choice of objects. Returned unresolved, so <Fields> sees the
  `$ref` and adds its name to `seen`; a name already on the path stops the
  recursion, which is how a cyclic spec (Variant ↔ Product) still renders.
*/
const nestedOf = (schema: Schema, schemas: Schemas, seen: ReadonlySet<string>): Schema | undefined => {
  const r = resolve(schema, schemas);
  if (r.name && seen.has(r.name)) return undefined;
  if (variantsOf(r.schema)) return schema;
  if (r.schema.type === "array" || (!r.schema.type && r.schema.items)) {
    if (!r.schema.items) return undefined;
    const next = r.name ? new Set(seen).add(r.name) : seen;
    return nestedOf(r.schema.items, schemas, next);
  }
  const props = r.schema.properties ?? {};
  if (Object.keys(props).length > 0) return schema;
  if (r.schema.additionalProperties && typeof r.schema.additionalProperties === "object") return schema;
  return undefined;
};

const Field = ({
  name,
  schema,
  schemas,
  required,
  deprecated,
  depth,
  seen,
  request,
  description,
}: {
  name: string;
  schema: Schema;
  schemas: Schemas;
  required: boolean;
  deprecated?: boolean;
  depth: number;
  seen: ReadonlySet<string>;
  request: boolean;
  description?: string;
}) => {
  const r = resolve(schema, schemas);
  const nested = depth < MAX_DEPTH ? nestedOf(schema, schemas, seen) : undefined;
  const nextSeen = r.name ? new Set(seen).add(r.name) : seen;
  return (
    <Param
      name={name}
      type={typeName(schema, schemas)}
      required={request && required}
      optional={request && !required}
      deprecated={deprecated || r.schema.deprecated === true}
    >
      <Text content={description ?? r.schema.description ?? schema.description} notes={notes(r.schema)} />
      {nested && (
        <Properties label={name} collapsed={depth >= 1} lazy={depth >= 1}>
          <Fields schema={nested} schemas={schemas} depth={depth + 1} seen={nextSeen} request={request} />
        </Properties>
      )}
    </Param>
  );
};

const RequestSections = ({ operation, schemas }: { operation: Operation; schemas: Schemas }) => {
  const groups = PARAM_GROUPS.map((g) => ({
    ...g,
    parameters: operation.parameters.filter((p) => p.in === g.in),
  })).filter((g) => g.parameters.length > 0);

  const body = operation.requestBody;
  const bodySchema = body?.schema ? resolve(body.schema, schemas).schema : undefined;
  const bodyIsObject =
    !!bodySchema && (bodySchema.type === "object" || !!bodySchema.properties || !!variantsOf(bodySchema));

  return (
    <>
      {groups.map((g) => (
        <Params key={g.in} title={g.title}>
          {g.parameters.map((p) => (
            <Field
              key={p.name}
              name={p.name}
              schema={p.schema}
              schemas={schemas}
              required={p.required}
              deprecated={p.deprecated}
              description={p.description}
              depth={0}
              seen={new Set()}
              request
            />
          ))}
        </Params>
      ))}
      {body?.schema && (
        <Params title={body.required ? "Request body" : "Request body (optional)"}>
          {body.description && (
            <div className="js-ref-body py-3">
              <Text content={body.description} />
            </div>
          )}
          {bodyIsObject ? (
            <Fields schema={body.schema} schemas={schemas} depth={0} seen={new Set()} request />
          ) : (
            <Field
              name="body"
              schema={body.schema}
              schemas={schemas}
              required={body.required}
              depth={0}
              seen={new Set()}
              request
            />
          )}
        </Params>
      )}
    </>
  );
};

const isSuccess = (status: string) => status.startsWith("2");

const ResponseSections = ({ operation, schemas }: { operation: Operation; schemas: Schemas }) => {
  const success = operation.responses.find((r) => isSuccess(r.status));
  const errors = operation.responses.filter((r) => !isSuccess(r.status));
  const nested = success?.schema ? nestedOf(success.schema, schemas, new Set()) : undefined;

  return (
    <>
      <Returns type={success?.schema ? typeName(success.schema, schemas) : undefined}>
        <Text content={success?.description ?? (success ? "No content." : "Nothing.")} />
        {nested && (
          <Properties label="response">
            <Fields schema={nested} schemas={schemas} depth={1} seen={new Set()} request={false} />
          </Properties>
        )}
      </Returns>
      {errors.length > 0 && (
        <Params title="Error responses">
          {errors.map((r) => (
            <Field
              key={r.status}
              name={r.status}
              schema={r.schema ?? {}}
              schemas={schemas}
              required={false}
              description={r.description}
              depth={1}
              seen={new Set()}
              request={false}
            />
          ))}
        </Params>
      )}
    </>
  );
};

const resultName = (operation: Operation, schemas: Schemas) => {
  const success = operation.responses.find((r) => isSuccess(r.status));
  const name = success?.schema ? resolve(success.schema, schemas).name : undefined;
  return name ? name.charAt(0).toLowerCase() + name.slice(1) : "data";
};

const ExampleCards = ({ operation, doc }: { operation: Operation; doc: TagDoc }) => {
  const { schemas } = doc;
  const url = `${doc.server}${operation.path}`;
  const headers = authHeaders(operation, doc.securitySchemes);
  const body = operation.requestBody?.schema
    ? (operation.requestBody.example ?? exampleOf(operation.requestBody.schema, schemas))
    : undefined;
  const success = operation.responses.find((r) => isSuccess(r.status));
  const response = success?.schema
    ? (success.example ?? exampleOf(success.schema, schemas))
    : undefined;

  return (
    <div className="flex flex-col gap-4">
      <CodeTabs syncKey="api-language" hideIcon>
        <CodeTabPanel language="shell" title="cURL" code={curlSnippet(operation, url, headers, body)} />
        <CodeTabPanel
          language="js"
          title="JavaScript"
          code={jsSnippet(operation, url, headers, body, resultName(operation, schemas))}
        />
      </CodeTabs>
      {response !== undefined && (
        <SyntaxHighlight
          language="json"
          title="Response"
          className="api-response"
          code={JSON.stringify(response, null, 2)}
        />
      )}
    </div>
  );
};

const OperationBlock = ({ operation, doc }: { operation: Operation; doc: TagDoc }) => (
  <Method
    id={operation.slug}
    title={operation.summary}
    signature={
      <>
        <MethodName method={operation.method} /> {operation.path}
      </>
    }
  >
    {operation.deprecated && (
      <p className="text-sm font-semibold text-orange-600 dark:text-orange-400">
        Deprecated. Do not use it in new integrations.
      </p>
    )}
    <Text content={operation.description} />
    <RequestSections operation={operation} schemas={doc.schemas} />
    <ResponseSections operation={operation} schemas={doc.schemas} />
    <Example>
      <ExampleCards operation={operation} doc={doc} />
    </Example>
  </Method>
);

const Endpoints = ({ operations }: { operations: Operation[] }) => (
  <div className="not-prose api-endpoints rounded-md border border-border text-sm">
    <div className="border-b border-border bg-secondary px-3 py-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
      Endpoints
    </div>
    <ul className="m-0 list-none p-0 font-mono text-[0.8125rem] leading-6">
      {operations.map((op) => (
        <li key={op.slug} className="m-0 px-3 py-1">
          <a href={`#${op.slug}`} className="flex gap-3 text-foreground no-underline hover:underline">
            <span className="w-14 shrink-0 text-end">
              <MethodName method={op.method} />
            </span>
            <span className="[overflow-wrap:anywhere]">{op.path}</span>
          </a>
        </li>
      ))}
    </ul>
  </div>
);

/*
  One tag of an API reference: the title and the endpoint list, then every
  operation as a <Method> block. Children go under the title.
*/
export const ApiPage = ({ data, children }: { data: TagDoc; children?: ReactNode }) => (
  <>
    <Head>
      <title>{`${data.name} · ${data.api.title}`}</title>
      <meta name="description" content={data.description ?? `${data.name} endpoints of the ${data.api.title}.`} />
    </Head>
    <Heading level={1}>{data.name}</Heading>
    <section className="grid grid-cols-1 items-start gap-x-10 gap-y-6 py-4 lg:grid-cols-[minmax(0,4fr)_minmax(0,3fr)]">
      <div className="js-ref-body min-w-0 text-[0.9375rem] leading-relaxed">
        <Text
          content={
            data.description ??
            `${data.operations.length} ${data.operations.length === 1 ? "endpoint" : "endpoints"} of the ${data.api.title}.`
          }
        />
        {children}
      </div>
      <Endpoints operations={data.operations} />
    </section>
    {data.operations.map((operation) => (
      <OperationBlock key={operation.slug} operation={operation} doc={data} />
    ))}
  </>
);
